import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import moment from 'moment';
import type { Action, Reducer } from 'redux';

import { IApplicationState, IAnalyticsAction, IAppThunkAction } from '.';
import OpenDate from '../interfaces/IOpenDate';
import Session from '../interfaces/ISession';
import SessionTime from '../interfaces/ISessionTime';
import { ICharge } from '../interfaces/ICharge';
import OpenDatesService from '../service/opendates';
import SessionsService from '../service/sessions';
import { appInsights } from "../Logging/appInsights";

import { toApiDateString } from '../helpers/dateFormatting';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import { PurchaseTypes } from './PurchaseTypes';
import { PLAYERS_SELECTED, IPlayersSelectedAction } from './Players';
import { GAME_SELECTED, IGameSelectedAction } from './Games';
import ISite from '../interfaces/ISite';

export interface ISessionState {
    openDates?: Map<string, OpenDate>;
    openDatesLoading: boolean;
    openDatesLoaded: boolean;
    // last date (YYYY-MM-DD) covered by the open dates loaded so far
    openDatesLoadedThrough?: string;
    // a further block of open dates is being loaded for calendar paging
    openDatesExtending: boolean;
    // the last extend returned nothing (or failed), so don't search any further ahead
    openDatesExhausted: boolean;
    sessionTimesLoading: boolean;
    sessionTimesLoaded: boolean;
    sessionTimes?: Array<SessionTime>;
    sessions?: Map<string, Session[]>;
    selectedDate?: string;
    selectedDateUI: boolean;
    selectedTime?: SessionTime;
    selectedTimeUI: boolean;
    selectedSession?: Session;
    selectedSessionUI: boolean;
    charge?: ICharge;
}

export const DATE_SELECTED: 'DATE_SELECTED' = 'DATE_SELECTED';
export const OPEN_DATES_LOADING: 'OPEN_DATES_LOADING' = 'OPEN_DATES_LOADING';
export const OPEN_DATES_LOADED: 'OPEN_DATES_LOADED' = 'OPEN_DATES_LOADED';
export const OPEN_DATES_EXTENDING: 'OPEN_DATES_EXTENDING' = 'OPEN_DATES_EXTENDING';
export const OPEN_DATES_EXTENDED: 'OPEN_DATES_EXTENDED' = 'OPEN_DATES_EXTENDED';
export const VIEWED_PACKAGE_DETAILS: 'VIEWED_PACKAGE_DETAILS' = 'VIEWED_PACKAGE_DETAILS';
export const VIEWED_PACKAGE_VIDEO: 'VIEWED_PACKAGE_VIDEO' = 'VIEWED_PACKAGE_VIDEO';
export const SESSION_TIMES_LOADING: 'SESSION_TIMES_LOADING' = 'SESSION_TIMES_LOADING';
export const SESSION_TIMES_LOADED: 'SESSION_TIMES_LOADED' = 'SESSION_TIMES_LOADED';
export const SESSION_TIME_SELECTED: 'SESSION_TIME_SELECTED' = 'SESSION_TIME_SELECTED';
export const SESSION_SELECTED: 'SESSION_SELECTED' = 'SESSION_SELECTED';
export const CLEAR_TIME_SELECTION: 'CLEAR_TIME_SELECTION' = 'CLEAR_TIME_SELECTION';
export const CLEAR_DATE_TIME_SELECTION: 'CLEAR_DATE_TIME_SELECTION' = 'CLEAR_DATE_TIME_SELECTION';

export interface ILoadOpenDatesAction extends Action {
    type: typeof OPEN_DATES_LOADING;
}

export interface IOpenDatesLoadedAction extends Action {
    type: typeof OPEN_DATES_LOADED;
    openDates?: Map<string, OpenDate>;
    loadedThrough?: string;
}

export interface IExtendOpenDatesAction extends Action {
    type: typeof OPEN_DATES_EXTENDING;
}

export interface IOpenDatesExtendedAction extends Action {
    type: typeof OPEN_DATES_EXTENDED;
    openDates: Map<string, OpenDate>;
    loadedThrough: string;
    exhausted: boolean;
}

export interface IPackageDetailsViewedAction extends IAnalyticsAction {
    type: typeof VIEWED_PACKAGE_DETAILS;
    session: Session;
}

export interface IPackageVideoViewedAction extends IAnalyticsAction {
    type: typeof VIEWED_PACKAGE_VIDEO;
    session: Session;
}

export interface IDateSelectedAction extends IAnalyticsAction {
    type: typeof DATE_SELECTED;
    date: string;
}

export interface ISessionTimesLoadingAction extends Action {
    type: typeof SESSION_TIMES_LOADING;
}

export interface ISessionTimesLoadedAction extends Action {
    type: typeof SESSION_TIMES_LOADED;
    sessions?: Map<string, Session[]>;
    sessionTimes?: Array<SessionTime>;
}

export interface ISessionTimeSelectedAction extends IAnalyticsAction {
    type: typeof SESSION_TIME_SELECTED;
    time: SessionTime;
}

export interface ISessionSelectedAction extends IAnalyticsAction {
    type: typeof SESSION_SELECTED;
    session: Session;
}

export interface IClearTimeSelection extends Action {
    type: typeof CLEAR_TIME_SELECTION;
}

export interface IClearDateTimeSelection extends Action {
    type: typeof CLEAR_DATE_TIME_SELECTION;
}

export type KnownSessionActions =
    | ILoadOpenDatesAction
    | IOpenDatesLoadedAction
    | IExtendOpenDatesAction
    | IOpenDatesExtendedAction
    | IPackageDetailsViewedAction
    | IPackageVideoViewedAction
    | IDateSelectedAction
    | ISessionTimesLoadingAction
    | ISessionTimesLoadedAction
    | ISessionTimeSelectedAction
    | ISessionSelectedAction
    | IClearTimeSelection
    | IClearDateTimeSelection
    | IPlayersSelectedAction
    | IGameSelectedAction;

let inFlightLoadOpenDates: AbortController | null = null;
let inFlightExtendOpenDates: AbortController | null = null;
let inFlightLoadSessionTimes: AbortController | null = null;

// params used by the most recent full open dates load, so extends search with exactly the same filters
let lastLoadOpenDatesParams: ILoadOpenDatesParams | null = null;

const toOpenDatesMap = (data: Array<OpenDate>, existing?: Map<string, OpenDate>): Map<string, OpenDate> => {
    const dates: Map<string, OpenDate> = new Map<string, OpenDate>(existing ?? []);

    data.forEach((openDate: OpenDate) => {
        dates.set(toApiDateString(moment(openDate.date).utc()), {
            siteId: openDate.siteId,
            date: openDate.date,
            status: openDate.status,
            bookedSlots: openDate.bookedSlots,
            totalSlots: openDate.totalSlots,
            pendingSlots: openDate.pendingSlots,
            remainingSlots: openDate.remainingSlots,
            maxTicketPrice: openDate.maxTicketPrice,
            minTicketPrice: openDate.minTicketPrice
        });
    });

    return dates;
};

// The initial load (monthSpan = 3) is anchored server side to the next *open time* on or after the requested
// start, not to the first date we get back, and covers that month plus the following 2. When the venue is open
// but the current month has no available dates, the first returned date is a month later than the anchor, so
// the range can't be derived from the results. The anchor is never before the requested start, so the range
// always covers at least through the end of start month + 2. Using that lower bound means anything uncovered
// is fetched by extendOpenDates instead of being assumed loaded (worst case is a re-fetch, never a skipped month).
const initialLoadedThrough = (requestedStart: string): string =>
    toApiDateString(moment(requestedStart, 'YYYY-MM-DD').startOf('month').add(2, 'months').endOf('month'));

export interface ILoadOpenDatesParams
{
    siteId: number;
    start?: string | undefined;
    end?: string | undefined;
    accessCode?: string | undefined;
    experienceId?: number | undefined;
    packageTypeId?: number | undefined;
    playerCount?: number | undefined;
}

export const actionCreators = {
    clearSelectedDateTime: () => ({ type: CLEAR_DATE_TIME_SELECTION } as IClearDateTimeSelection),

    loadOpenDates:
        (params: ILoadOpenDatesParams ): IAppThunkAction<KnownSessionActions> =>
            (dispatch, _getState) => {

                // cancel previous request if one is running, including any calendar extend for the old filters
                if (inFlightLoadOpenDates) inFlightLoadOpenDates.abort();
                if (inFlightExtendOpenDates) inFlightExtendOpenDates.abort();

                // create per-call controller and hand over as current
                const controller = new AbortController();
                inFlightLoadOpenDates = controller;

                if (params.start === undefined) {
                    params.start = toApiDateString(moment());
                }
                if (params.end === undefined) {
                    params.end = toApiDateString(moment().add(2, 'months').endOf('month'));
                }

                lastLoadOpenDatesParams = { ...params };

                OpenDatesService.Search(
                    params.siteId,
                    params.start,
                    params.end,
                    params.accessCode,
                    params.packageTypeId === 1 ? params.experienceId : undefined,
                    params.packageTypeId,
                    params.playerCount,
                    controller.signal
                )
                    .then((data) => {
                        dispatch({
                            type: OPEN_DATES_LOADED,
                            openDates: toOpenDatesMap(data),
                            loadedThrough: initialLoadedThrough(params.start as string),
                        });
                    })
                    .catch((error: Error) => {
                        if ((error as any)?.name === 'AbortError') {
                            console.log("ignore cancelled api request in loadOpenDates", error);
                            return; // ignore cancelled
                        }

                        dispatch({ type: OPEN_DATES_LOADED, openDates: new Map<string, OpenDate>() });
                        appInsights?.trackException(
                            { error: new Error(`Error loading open dates`), severityLevel: 3 },
                            { exception: error, sourceFile: "Sessions.ts", product: "booking" });
                    })
                    .finally(() => {
                        // only clear if this request is still the active one
                        if (inFlightLoadOpenDates === controller) inFlightLoadOpenDates = null;
                    });
                dispatch({ type: OPEN_DATES_LOADING });
        },
    // Loads the next 3 month block of open dates after those already loaded, merging them in without
    // touching the current date/time selection. Used when the calendar is paged past the loaded months.
    extendOpenDates:
        (): IAppThunkAction<KnownSessionActions> =>
            (dispatch, getState) => {
                const { openDatesLoadedThrough, openDatesExtending, openDatesExhausted, openDatesLoading } = getState().session;

                if (!lastLoadOpenDatesParams || !openDatesLoadedThrough) return;
                if (openDatesLoading || openDatesExtending || openDatesExhausted) return;

                const controller = new AbortController();
                inFlightExtendOpenDates = controller;

                const start = moment(openDatesLoadedThrough, 'YYYY-MM-DD').add(1, 'day');
                const end = start.clone().add(2, 'months').endOf('month');
                const loadedThrough = toApiDateString(end);
                const params = lastLoadOpenDatesParams;

                OpenDatesService.Search(
                    params.siteId,
                    toApiDateString(start),
                    loadedThrough,
                    params.accessCode,
                    params.packageTypeId === 1 ? params.experienceId : undefined,
                    params.packageTypeId,
                    params.playerCount,
                    controller.signal,
                    true
                )
                    .then((data) => {
                        dispatch({
                            type: OPEN_DATES_EXTENDED,
                            openDates: toOpenDatesMap(data, getState().session.openDates),
                            loadedThrough,
                            exhausted: data.length === 0,
                        });
                    })
                    .catch((error: Error) => {
                        if ((error as any)?.name === 'AbortError') {
                            console.log("ignore cancelled api request in extendOpenDates", error);
                            return; // ignore cancelled
                        }

                        // stop paging further rather than retrying a failing search
                        dispatch({
                            type: OPEN_DATES_EXTENDED,
                            openDates: getState().session.openDates ?? new Map<string, OpenDate>(),
                            loadedThrough: openDatesLoadedThrough,
                            exhausted: true,
                        });
                        appInsights?.trackException(
                            { error: new Error(`Error extending open dates`), severityLevel: 3 },
                            { exception: error, sourceFile: "Sessions.ts", product: "booking" });
                    })
                    .finally(() => {
                        if (inFlightExtendOpenDates === controller) inFlightExtendOpenDates = null;
                    });
                dispatch({ type: OPEN_DATES_EXTENDING });
        },
    loadSessionTimes:
        (date: moment.Moment, experienceId: number | undefined): IAppThunkAction<KnownSessionActions> =>
            (dispatch, getState) => {

            // cancel previous request if one is running
            if (inFlightLoadSessionTimes) inFlightLoadSessionTimes.abort();

            // create per-call controller and hand over as current
            const controller = new AbortController();
            inFlightLoadSessionTimes = controller;


            const appState: IApplicationState = getState();
            const site = appState.sites.selectedSite;
            const accessCode = appState.sites.accessCode;
            const players = appState.players.selectedPlayers;
            const selectedPurchaseType = appState.purchaseType.selectedPurchaseType;
            const priceSet = appState.booking.priceSets?.find((p) => p.venue === site?.urlKey);

            const packageTypeId = selectedPurchaseType === PurchaseTypes.Booking ? 1 : 2;

            if (appState && site && appState.players.selectedPlayers && appState.session.selectedDate) {
                SessionsService.GetSessionTimes(
                    site,
                    date,
                    players,
                    accessCode,
                    experienceId,
                    packageTypeId,
                    priceSet?.code,
                    controller.signal
                )
                    .then((data) => {
                        const sessions: Map<string, Session[]> = new Map<string, Session[]>();
                        const groupedSessions = groupBy(data, (session: Session) => session.startTime);

                        //both sessions and session times in one map
                        const sessionTimes: Array<SessionTime> = map(
                            groupedSessions,
                            (value: Session[], key: string) => {
                                sessions.set(key, value);


                                let openTimeMinimumPlayers: number | undefined;
                                let slotsRemaining: number | undefined;
                                let slotMinPrice: number | undefined;
                                let slotMaxPrice: number | undefined;

                                for (const s of value) {
                                    // openTimeMinimumPlayers
                                    const players = s.bookedSlots > 0 ? 1 : s.openTimeMinimumPlayers;
                                    if (openTimeMinimumPlayers === undefined || players < openTimeMinimumPlayers) {
                                        openTimeMinimumPlayers = players;
                                    }

                                    // slotsRemaining
                                    const slots = s.isPrivate ? 0 : s.remainingSlots;
                                    if (slotsRemaining === undefined || slots > slotsRemaining) {
                                        slotsRemaining = slots;
                                    }

                                    // price matching
                                    const matchedPriceTier = s.package.packageTierPrices.find(
                                        (ptp) => ptp.priceTierType.priceTierTypeId === s.priceTierTypeId
                                    );

                                    if (matchedPriceTier) {
                                        const price = matchedPriceTier.price.listPrice;

                                        if (slotMinPrice === undefined || price < slotMinPrice) {
                                            slotMinPrice = price;
                                        }
                                        if (slotMaxPrice === undefined || price > slotMaxPrice) {
                                            slotMaxPrice = price;
                                        }
                                    }
                                }

                                return {
                                    time: key,
                                    openTimeMinimumPlayers,
                                    slotsRemaining,
                                    slotMinPrice,
                                    slotMaxPrice,
                                } as SessionTime;
                            }
                        );


                        dispatch({ type: SESSION_TIMES_LOADED, sessions: sessions, sessionTimes: sessionTimes });
                    })
                    .catch((error: Error) => {
                        if ((error as any)?.name === 'AbortError') {
                            console.log("ignore cancelled api request in loadSessionTimes", error);
                            return; // ignore cancelled
                        }

                        dispatch({ type: SESSION_TIMES_LOADED, sessions: undefined, sessionTimes: undefined });
                         appInsights?.trackException(
                             { error: new Error(`Error loading session times`), severityLevel: 4},
                             { exception: error, sourceFile: "Sessions.ts", product: "booking" });
                    }).finally(() => {
                        // only clear if this request is still the active one
                        if (inFlightLoadSessionTimes === controller) inFlightLoadSessionTimes = null;
                    });
                dispatch({ type: SESSION_TIMES_LOADING });
            }
        },

    setSessionById: (site: ISite, sessionId: number): IAppThunkAction<KnownSessionActions> => (dispatch, _getState) => {
        SessionsService.GetSessionsById(site, sessionId).then((data) => {
            const sessionData: Session = data as Session
            dispatch({
                type: SESSION_SELECTED,
                session: sessionData,
            } as ISessionSelectedAction)
        })
    },

    postPackageVideoViewedAction:
        (session: Session): IAppThunkAction<KnownSessionActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();

            const siteCode = appState.sites.selectedSite?.code ?? ('' as string);
            const appliedCodes = appState.booking.appliedCodes;

            const ticketCount = appState.players.selectedPlayers ?? (0 as number);

            dispatch({
                type: VIEWED_PACKAGE_VIDEO,
                session: session,
                googleTagManagerEvent: GoogleTagManagerEvents.ViewedPackageVideo(
                    session,
                    siteCode,
                    ticketCount,
                    appliedCodes
                ),
            } as IPackageVideoViewedAction);
        },

    postPackageDetailsViewedAction:
        (session: Session): IAppThunkAction<KnownSessionActions> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();

            const siteCode = appState.sites.selectedSite?.code ?? ('' as string);
            const appliedCodes = appState.booking.appliedCodes;

            const ticketCount = appState.players.selectedPlayers ?? (0 as number);

            dispatch({
                type: VIEWED_PACKAGE_DETAILS,
                session: session,
                googleTagManagerEvent: GoogleTagManagerEvents.ViewedPackageDetails(
                    session,
                    siteCode,
                    ticketCount,
                    appliedCodes
                ),
            } as IPackageDetailsViewedAction);
        },

    postDateSelectedAction: (date: string) =>
        ({
            type: DATE_SELECTED,
            date: date,
            googleTagManagerEvent: GoogleTagManagerEvents.SessionDateSelected(date),
        } as IDateSelectedAction),

    postTimeSelectedAction: (time: SessionTime) =>
        ({
            type: SESSION_TIME_SELECTED,
            time: time,
            googleTagManagerEvent: GoogleTagManagerEvents.SessionTimeSelected(time),
        } as ISessionTimeSelectedAction),

    clearSelectedTime: () => ({ type: CLEAR_TIME_SELECTION }),
    postSessionSelectedAction:
        (session: Session): IAppThunkAction<KnownSessionActions> =>
        (dispatch, _getState) => {

            dispatch({
                type: SESSION_SELECTED,
                session: session,
                googleTagManagerEvent: GoogleTagManagerEvents.SessionPackageSelected(session),
            } as ISessionSelectedAction);
        },
};

const unloadedState: ISessionState = {
    openDatesLoaded: false,
    openDates: undefined,
    openDatesLoading: false,
    openDatesLoadedThrough: undefined,
    openDatesExtending: false,
    openDatesExhausted: false,
    sessions: undefined,
    sessionTimes: undefined,
    selectedDate: undefined,
    sessionTimesLoaded: false,
    sessionTimesLoading: false,
    selectedSession: undefined,
    selectedDateUI: false,
    selectedTimeUI: false,
    selectedSessionUI: false,
};

export const reducer: Reducer<ISessionState> = (
    state: ISessionState | undefined,
    incomingAction: Action
): ISessionState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action: KnownSessionActions = incomingAction as KnownSessionActions;
    switch (action.type) {
        case GAME_SELECTED:
            return {
                ...state,
                selectedDate: undefined,
                selectedSession: undefined,
                selectedTime: undefined,
                selectedDateUI: false,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        case PLAYERS_SELECTED: {
            const newCount = action.players;
            const sessionFits =
                state.selectedSession &&
                state.selectedSession.remainingSlots >= newCount &&
                state.selectedSession.openTimeMinimumPlayers <= newCount;
            const timeFits =
                state.selectedTime &&
                state.selectedTime.slotsRemaining >= newCount &&
                state.selectedTime.openTimeMinimumPlayers <= newCount;

            if (sessionFits && timeFits) {
                return state;
            }

            return {
                ...state,
                selectedDate: undefined,
                selectedSession: undefined,
                selectedTime: undefined,
                selectedDateUI: false,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        }
        case CLEAR_TIME_SELECTION:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessions: state.sessions,
                sessionTimes: state.sessionTimes,
                selectedDate: state.selectedDate,
                sessionTimesLoaded: true,
                sessionTimesLoading: false,
                selectedSession: undefined,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        case CLEAR_DATE_TIME_SELECTION:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessions: state.sessions,
                sessionTimes: state.sessionTimes,
                selectedDate: undefined,
                sessionTimesLoaded: true,
                sessionTimesLoading: false,
                selectedSession: undefined,
                selectedDateUI: false,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        case OPEN_DATES_LOADING:
            return {
                openDatesLoaded: false,
                openDatesLoading: true,
                openDates: undefined,
                openDatesLoadedThrough: undefined,
                openDatesExtending: false,
                openDatesExhausted: false,
                sessions: undefined,
                sessionTimes: undefined,
                selectedDate: undefined,
                sessionTimesLoading: false,
                sessionTimesLoaded: false,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: state.selectedTimeUI,
                selectedSessionUI: state.selectedSessionUI,
            };
        case OPEN_DATES_LOADED:
            return {
                openDatesLoaded: true,
                openDatesLoading: false,
                openDates: action.openDates,
                openDatesLoadedThrough: action.loadedThrough,
                openDatesExtending: false,
                openDatesExhausted: false,
                sessions: undefined,
                sessionTimes: undefined,
                selectedDate: undefined,
                sessionTimesLoading: false,
                sessionTimesLoaded: false,
                selectedDateUI: false,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        case OPEN_DATES_EXTENDING:
            return {
                ...state,
                openDatesExtending: true,
            };
        case OPEN_DATES_EXTENDED:
            return {
                ...state,
                openDates: action.openDates,
                openDatesLoadedThrough: action.loadedThrough,
                openDatesExtending: false,
                openDatesExhausted: action.exhausted,
            };
        case DATE_SELECTED:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessions: undefined,
                sessionTimes: undefined,
                selectedDate: action.date,
                sessionTimesLoading: false,
                sessionTimesLoaded: false,
                selectedDateUI: true,
                selectedTimeUI: false,
                selectedSessionUI: false,
            };
        case SESSION_TIMES_LOADING:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                sessionTimesLoading: true,
                sessionTimesLoaded: false,
                selectedDate: state.selectedDate,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessionTimes: undefined,
                sessions: undefined,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: state.selectedTimeUI,
                selectedSessionUI: state.selectedSessionUI,
            };
        case SESSION_TIMES_LOADED:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                sessionTimesLoading: false,
                sessionTimesLoaded: true,
                selectedDate: state.selectedDate,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessionTimes: action.sessionTimes,
                sessions: action.sessions,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: state.selectedTimeUI,
                selectedSessionUI: false,
                selectedSession: undefined,
            };
        case SESSION_TIME_SELECTED:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                sessionTimesLoading: state.sessionTimesLoading,
                sessionTimesLoaded: state.sessionTimesLoaded,
                selectedDate: state.selectedDate,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessionTimes: state.sessionTimes,
                sessions: state.sessions,
                selectedTime: action.time,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: true,
                selectedSessionUI: false,
                selectedSession: undefined,
            };
        case SESSION_SELECTED:
            return {
                openDatesLoaded: state.openDatesLoaded,
                openDatesLoading: state.openDatesLoading,
                sessionTimesLoading: state.sessionTimesLoading,
                sessionTimesLoaded: state.sessionTimesLoaded,
                selectedDate: state.selectedDate,
                openDates: state.openDates,
                openDatesLoadedThrough: state.openDatesLoadedThrough,
                openDatesExtending: state.openDatesExtending,
                openDatesExhausted: state.openDatesExhausted,
                sessionTimes: state.sessionTimes,
                sessions: state.sessions,
                selectedTime: state.selectedTime,
                selectedSession: action.session,
                selectedDateUI: state.selectedDateUI,
                selectedTimeUI: state.selectedTimeUI,
                selectedSessionUI: true,
            };
        default:
            return state;
    }
};
